import { useCallback, useState } from 'react'
import classnames from 'classnames'

import { Button } from '../Button/Button'
import { type DocumentConnectorIntegration } from '../../store/documentConnectors/documentConnectors'
import styles from './FileBrowser.module.scss'

interface LinkFolderBannerProps {
  contactName: string
  linkedFolderId?: string | null
  currentFolderId?: string | null
  currentFolderName?: string | null
  currentFolderIntegration?: DocumentConnectorIntegration
  onLinkFolder: (integration: DocumentConnectorIntegration) => Promise<void>
}

interface LinkedFolder {
  id: string
  name: string
}

export const LinkFolderBanner = ({
  contactName,
  linkedFolderId,
  currentFolderId,
  currentFolderName,
  currentFolderIntegration,
  onLinkFolder,
}: LinkFolderBannerProps) => {
  const [justLinkedFolder, setJustLinkedFolder] = useState<LinkedFolder | null>(null)
  const [isLinking, setIsLinking] = useState<boolean>(false)

  const handleLinkFolder = useCallback(async () => {
    if (!currentFolderIntegration || !currentFolderId) {
      return
    }
    setIsLinking(true)
    try {
      await onLinkFolder(currentFolderIntegration)
      setJustLinkedFolder({ id: currentFolderId, name: currentFolderName ?? '' })
    } catch {
      // ignore; the banner stays so the user can retry
    } finally {
      setIsLinking(false)
    }
  }, [currentFolderIntegration, currentFolderId, currentFolderName, onLinkFolder])

  const showWarning = !linkedFolderId && currentFolderId && currentFolderIntegration
  const showSuccess = justLinkedFolder != null && justLinkedFolder.id === currentFolderId

  if (showSuccess) {
    return (
      <div className={classnames(styles.linkBanner, styles.linkBannerSuccess)}>
        <div className='d-flex align-items-center gap-2'>
          <i className='bi bi-check-circle-fill' />
          <span>
            <strong>&ldquo;{justLinkedFolder.name}&rdquo;</strong> successfully linked to {contactName} as default folder.
          </span>
        </div>
      </div>
    )
  }

  if (!showWarning) {
    return null
  }

  return (
    <div className={classnames(styles.linkBanner, styles.linkBannerWarning)}>
      <span>There is no default folder linked for {contactName}. Would you like to link this folder?</span>
      <Button className={styles.linkBannerAction} onClick={() => handleLinkFolder()} loading={isLinking}>
        {isLinking ? null : <i className='bi bi-link-45deg' />}
        Link current folder
      </Button>
    </div>
  )
}
